import { ErrorOutlineRounded } from '@mui/icons-material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { Box, Typography } from '@mui/material';
import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import { CollapsibleTable } from '../../components/CollapsibleTable';
import { Disclaimer } from '../../components/Disclaimer';
import { MainContainer } from '../../components/MainContainerComp';
import ScrollIndicatorsComp from '../../components/ScrollIndicatorsComp/ScrollIndicatorsComp';
import { CurrentDespatchItem, ICurrentDespatchData } from './CurrentDispatchData';
import { NOT_CONFIRMED } from './CurrentDispatchModels';
import { CurrentDispatchNotes } from './CurrentDispatchNotes';
import { checkIconStyle, disclaimerContainerStyle, disclaimerContentStyle, disclaimerStyle, iconStyle, iconWrapperStyle, noDataContainerStyle, notConfirmedIconStyle } from './styled';

export interface CurrentDispatchProps {
  data: ICurrentDespatchData;
  handleSubmit: (id: number) => void;
  isTelephoneBoard: boolean;
  setShowDisclaimer: () => void;
  showDisclaimer: boolean
}

export const CurrentDispatch: FC<CurrentDispatchProps> = ({
  data,
  handleSubmit,
  isTelephoneBoard,
  showDisclaimer,
  setShowDisclaimer
}) => {
  /* 
    Note that data may be type { message: string } which isn't described in CurrentDispatchData.tsx
    In this scenario, currDespatch should be treated as undefined because WebApi uses message to indicate 
    "no current dispatch"
  */
  const { currentDespatch } = data;

  const getLinkToSlipDetails = (id: number, groupId: number, isGroup: boolean): string =>
    data.currentDespatch ? `/slip-preview-slip/${isGroup ? `groupId/${groupId}` : `${id}`}` : '';

  const getIconElement = (orderPickupNotes: string) => orderPickupNotes === NOT_CONFIRMED ?
    <HighlightOffIcon sx={notConfirmedIconStyle} /> : <CheckCircleOutlineIcon sx={checkIconStyle} />;

  const formattedShift = (originalString: string) => {
    const hasSpecialStart = originalString.includes('Special Start :');
    const specialStartFormatted = originalString.replace(/\((.*?)\)/g, '###$1###');
    return hasSpecialStart ? specialStartFormatted : originalString
  }

  const getRows = (despatch: CurrentDespatchItem) => ([
    {
      ...(despatch?.title === 'You are dispatched to:' && {
        label: 'Slip:',
        slip: (
          <Box sx={{ textAlign: 'end' }}>
            <Link style={iconStyle} to={getLinkToSlipDetails(despatch?.id, despatch?.groupId, despatch.isGroup)}>
              <img
                style={{ position: 'relative', top: '5px' }}
                src={`/assets/images/${despatch.colour?.toLowerCase()}_slip.png`}
                alt="SLIP PREVIEW"
                width={30}
                height={60}
              />
            </Link>
          </Box>),
      })
    },
    { label: 'Order Date:', orderDate: despatch?.orderDate },
    {
      label: 'Shift:', shift: (
        <Box>
          {formattedShift(despatch?.shift).split('###').map((part, i) =>
            i % 2 === 1 ? (
              <Box component="span" key={i}>
                (<Typography className='red' component="span">{part}</Typography>)
              </Box>
            ) : part)
          }
        </Box>
      )
    },
    { label: 'Location:', location: despatch?.location },
    { label: 'Vessel:', vessel: despatch?.vessel },
    { label: 'Job:', job: despatch?.job },
    { ...(despatch?.notes) && { label: 'Notes:', notes: despatch.notes } },
    { label: 'Warnings:', warnings: despatch?.warnings },
    {
      ...despatch?.orderPickupNotes && {
        label: 'Order Pickup Status:',
        orderPickupNotes: (
          <Typography sx={iconWrapperStyle}>
            {getIconElement(despatch.orderPickupNotes)} {despatch?.orderPickupNotes}
          </Typography>
        )
      }
    }
  ]);

  return (
    <MainContainer hasGreyBg>
      <Box
        sx={disclaimerStyle}
        onClick={setShowDisclaimer}
      >
        <ErrorOutlineRounded sx={{ mr: "3px" }} />
        <Typography
          data-automation="disclaimer-label"
        >
          Disclaimer
        </Typography>
      </Box>
      {showDisclaimer &&
        <Box sx={disclaimerContainerStyle}>
          <Box sx={disclaimerContentStyle}>
            <Disclaimer
              onClick={setShowDisclaimer}
              title="Disclaimer"
              text={[
                'Current dispatch information is real-time. This information may change at any time. Please check your work status frequently!',
                'Dispatch, callback, and other information displayed here are provided for informational purposes only.',
                'The Dispatch office an/or actual site personnel continue to be responsible for dispatch, callback, and related information.'
              ]}
            />
          </Box>
        </Box>
      }
      {
        currentDespatch ? currentDespatch.map((despatch, i) => (
          <React.Fragment key={i}>
            <Typography
              sx={{ mt: 2 }}
              component={'div'}
              variant='container'
              data-automation="data-container"
            >
              <ScrollIndicatorsComp>
                <CollapsibleTable
                  noDataMsg='You do not have Dispatch or Callback records for the Current Dispatch.'
                  isOneLevel={true}
                  cols={[{ title: data.currentDespatch && despatch ? despatch?.title : '' }]}
                  rows={getRows(despatch)}
                  noColsDivider={true}
                />
              </ScrollIndicatorsComp>
            </Typography>
            {despatch?.orderPickupExtraNotes.title && (
              <CurrentDispatchNotes
                orderDetailId={despatch.id}
                isTelephoneBoard={isTelephoneBoard}
                handleSubmit={() => handleSubmit(despatch.id)}
                note={despatch?.orderPickupExtraNotes}
              />
            )}
          </React.Fragment>
        )) : (
          <Typography
            sx={noDataContainerStyle}
            component={'div'}
            variant='container'
            data-automation="data-container"
          >
            <CollapsibleTable
              noDataMsg={data.message}
              isOneLevel={true}
              cols={[]}
              rows={[]}
            />
          </Typography>
        )
      }
    </MainContainer>
  )
}